import type {
	CellClassParams,
	ColDef,
	ColGroupDef,
	ValueGetterParams,
} from "ag-grid-community";
import {
	formatSpecRevision,
	type SectionExportItem,
	type SectionExportSelection,
	type SectionExportSpec,
	type SectionExportSpecItem,
	type SectionsExportData,
	toExcelDateTime,
} from "../../models/SectionExport";
import {
	EXCEL_STYLE,
	type ExcelNumberStyle,
	isBanded,
	NOT_APPLICABLE,
} from "./sectionExportStyles";
import { EXPORT_MILLS } from "./sectionsSheet";

export interface SpecificationsSheetRow {
	SectionName: string;
	Asset: string;
	Spec: SectionExportSpec | null;
	ItemsById: Map<number, SectionExportSpecItem>;
	GroupIndex: number;
}

const sectionKey = ({ AssetId, SectionId }: SectionExportSelection) =>
	`${AssetId}:${SectionId}`;

const millLabel = (assetId: number) =>
	EXPORT_MILLS.find((mill) => mill.millId === assetId)?.label ?? "";

export const buildSpecificationsSheetRows = (
	data: SectionsExportData,
	selections: SectionExportSelection[],
): SpecificationsSheetRow[] => {
	const sectionsByKey = new Map(
		data.sections.map((section) => [sectionKey(section), section]),
	);
	const specsByKey = new Map(
		data.specs.map((spec) => [sectionKey(spec), spec]),
	);
	const itemsByKey = new Map<string, Map<number, SectionExportSpecItem>>();
	for (const item of data.specItems) {
		const key = sectionKey(item);
		const items = itemsByKey.get(key) ?? new Map();
		items.set(item.ProfileItemId, item);
		itemsByKey.set(key, items);
	}
	return selections
		.flatMap((selection) => {
			const key = sectionKey(selection);
			const section = sectionsByKey.get(key);
			return section === undefined ? [] : [{ key, section }];
		})
		.map(({ key, section }, groupIndex) => ({
			SectionName: section.SectionName,
			Asset: millLabel(section.AssetId),
			Spec: specsByKey.get(key) ?? null,
			ItemsById: itemsByKey.get(key) ?? new Map(),
			GroupIndex: groupIndex,
		}));
};

const specColumn = (
	headerName: string,
	width: number,
	cellClass: string[],
	getValue: (spec: SectionExportSpec) => string | number | null,
): ColDef<SpecificationsSheetRow> => ({
	headerName,
	width,
	valueGetter: (params: ValueGetterParams<SpecificationsSheetRow>) =>
		params.data?.Spec ? getValue(params.data.Spec) : null,
	cellClass,
	cellClassRules: { [EXCEL_STYLE.BAND]: isBanded<SpecificationsSheetRow> },
});

const specNumberColumn = (
	headerName: string,
	width: number,
	style: ExcelNumberStyle,
	getValue: (spec: SectionExportSpec) => number | null,
) => specColumn(headerName, width, [style], getValue);

const isItemNotApplicable =
	(profileItemId: number) =>
	(params: CellClassParams<SpecificationsSheetRow>) =>
		params.data?.Spec != null && !params.data.ItemsById.has(profileItemId);

const itemValueColumn = (
	profileItemId: number,
	headerName: string,
	getValue: (item: SectionExportSpecItem) => string | number | null,
): ColDef<SpecificationsSheetRow> => ({
	headerName,
	width: 75,
	valueGetter: (params: ValueGetterParams<SpecificationsSheetRow>) => {
		if (!params.data?.Spec) return null;
		const item = params.data.ItemsById.get(profileItemId);
		return item ? getValue(item) : NOT_APPLICABLE;
	},
	cellClass: EXCEL_STYLE.PRECISE,
	cellClassRules: {
		[EXCEL_STYLE.BAND]: isBanded<SpecificationsSheetRow>,
		[EXCEL_STYLE.NOT_APPLICABLE]: isItemNotApplicable(profileItemId),
	},
});

const itemColumnGroup = (
	item: SectionExportItem,
): ColGroupDef<SpecificationsSheetRow> => ({
	headerName: `${millLabel(item.AssetId)} / ${item.CategoryName} / ${item.Code} - ${item.Name}`,
	children:
		item.ValueTypeCode === "A"
			? [
					{
						...itemValueColumn(item.ProfileItemId, "Applies", () => "Yes"),
						cellClass: EXCEL_STYLE.TEXT,
					},
				]
			: [
					itemValueColumn(item.ProfileItemId, "Min", (value) => value.Ltl),
					itemValueColumn(
						item.ProfileItemId,
						"Target",
						(value) => value.Nominal,
					),
					itemValueColumn(item.ProfileItemId, "Max", (value) => value.Utl),
				],
});

const COMMENTS_MIN_WIDTH = 260;
const COMMENTS_MAX_WIDTH = 900;
const COMMENTS_CHAR_WIDTH = 7;
const COMMENTS_PADDING = 16;

const resolveCommentsWidth = (specs: SectionExportSpec[]) => {
	const longestComment = Math.max(
		0,
		...specs.map((spec) => spec.Comments?.length ?? 0),
	);
	return Math.min(
		COMMENTS_MAX_WIDTH,
		Math.max(
			COMMENTS_MIN_WIDTH,
			longestComment * COMMENTS_CHAR_WIDTH + COMMENTS_PADDING,
		),
	);
};

export const buildSpecificationsSheetColumns = (
	items: SectionExportItem[],
	specs: SectionExportSpec[],
): (ColDef<SpecificationsSheetRow> | ColGroupDef<SpecificationsSheetRow>)[] => [
	{
		headerName: "Section",
		width: 160,
		pinned: "left",
		valueGetter: (params) => params.data?.SectionName ?? null,
		cellClass: [EXCEL_STYLE.TEXT, EXCEL_STYLE.SECTION],
		cellClassRules: { [EXCEL_STYLE.BAND]: isBanded<SpecificationsSheetRow> },
	},
	{
		headerName: "Asset",
		width: 80,
		valueGetter: (params) => params.data?.Asset ?? null,
		cellClass: [EXCEL_STYLE.TEXT, EXCEL_STYLE.CENTER],
		cellClassRules: { [EXCEL_STYLE.BAND]: isBanded<SpecificationsSheetRow> },
	},
	specColumn("Rev", 60, [EXCEL_STYLE.TEXT, EXCEL_STYLE.CENTER], (spec) =>
		formatSpecRevision(spec.Rev, spec.SubRev),
	),
	specColumn("Effective Date", 95, [EXCEL_STYLE.DATE], (spec) =>
		toExcelDateTime(spec.EffectiveDate),
	),
	specColumn(
		"Print No",
		95,
		[EXCEL_STYLE.TEXT, EXCEL_STYLE.CENTER],
		(spec) => spec.PrintNo,
	),
	specNumberColumn(
		"Area (sq in)",
		85,
		EXCEL_STYLE.PRECISE,
		(spec) => spec.Area,
	),
	specNumberColumn(
		"Target LBs / Foot",
		95,
		EXCEL_STYLE.PRECISE,
		(spec) => spec.TargetLbsPerFoot,
	),
	specNumberColumn(
		"Min LBs / Foot",
		95,
		EXCEL_STYLE.PRECISE,
		(spec) => spec.MinLbsPerFoot,
	),
	specNumberColumn(
		"Max LBs / Foot",
		95,
		EXCEL_STYLE.PRECISE,
		(spec) => spec.MaxLbsPerFoot,
	),
	specNumberColumn(
		"Leg Delta Max",
		90,
		EXCEL_STYLE.PRECISE,
		(spec) => spec.LegDeltaMax,
	),
	...items.map(itemColumnGroup),
	specColumn(
		"Comments",
		resolveCommentsWidth(specs),
		[EXCEL_STYLE.TEXT],
		(spec) => spec.Comments,
	),
];
